import { supabase } from "@/integrations/supabase/client";
import { logAiAdvisoryEvent } from "./aiAdvisory";

export interface PhotoComparePair {
  room_name: string;
  task_text: string;
  reference_url: string;
  after_url: string;
}

export interface CompareCleaningPhotosResult {
  ok: boolean;
  comparisons?: Array<{
    room_name: string;
    task_text: string;
    likely_damage: boolean;
    likely_missing: boolean;
    confidence: number;
    rationale: string;
  }>;
  error?: string;
}

/**
 * Advisory only — call after submission when reference + after URLs exist.
 *
 * This never blocks submission, never closes a task, never auto-charges. The
 * caller stores comparisons in `cleaning_submissions.ai_reconciliation` for
 * an owner to review. Every invoke is logged via `aiAdvisory`.
 */
export async function compareCleaningPhotosForSubmission(
  pairs: PhotoComparePair[],
  context?: { propertyId?: string | null; cleaningTaskId?: string | null }
): Promise<CompareCleaningPhotosResult> {
  if (import.meta.env.VITE_AI_PHOTO_RECONCILIATION !== "true") {
    return { ok: true, comparisons: [] };
  }
  if (pairs.length === 0) return { ok: true, comparisons: [] };
  const t0 = performance.now();
  const { data, error } = await supabase.functions.invoke("compare-cleaning-photos", {
    body: { pairs },
  });
  if (error) {
    console.warn("[compareCleaningPhotosForSubmission]", error.message);
    logAiAdvisoryEvent({
      kind: "compare_cleaning_photos",
      propertyId: context?.propertyId ?? null,
      cleaningTaskId: context?.cleaningTaskId ?? null,
      durationMs: performance.now() - t0,
      outcome: "error",
      extra: { pairCount: pairs.length },
    });
    return { ok: false, error: error.message };
  }
  const result = (data ?? { ok: false }) as CompareCleaningPhotosResult;
  const comparisons = result.comparisons ?? [];
  const avg =
    comparisons.length > 0
      ? comparisons.reduce((s, c) => s + (typeof c.confidence === "number" ? c.confidence : 0), 0) /
        comparisons.length
      : null;
  logAiAdvisoryEvent({
    kind: "compare_cleaning_photos",
    propertyId: context?.propertyId ?? null,
    cleaningTaskId: context?.cleaningTaskId ?? null,
    confidence: avg,
    durationMs: performance.now() - t0,
    outcome: result.ok ? "ok" : "error",
    extra: {
      pairCount: pairs.length,
      flaggedDamage: comparisons.filter((c) => c.likely_damage).length,
      flaggedMissing: comparisons.filter((c) => c.likely_missing).length,
    },
  });
  return result;
}
